import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../appState';
import { VehicleOption } from '../Redux/ConditionEntities';
import SilverExecSelector from '../../../widgets/silver-exec-selector/SilverExecSelector';
import { Box } from '@mui/material';
import { Dispatch } from '../../Dispatch';
import { BookingTimeV2 } from '../../GbBooking/BookingV2Entities';
import { ServiceCheckStatus } from '../../Booking/BookingEntities';

interface PropsFromStore {
    SelectedCondition: VehicleOption;
    IsPriceGuaranteeSelected: boolean;
    SilverExec: { Passengers: number; Luggage: number; FlightNumber: string; } | null;
    SilverExecXl: { Passengers: number; Luggage: number; FlightNumber: string; } | null;
    BookingTime: BookingTimeV2;
    PickupTimeZoneId: string | null;
    PickupZoneId: number | null;
}

/**
 * Standalone section that displays the Silver Exec selector between vehicle selection and contact details.
 * Only visible when Silver Service Exec or XL is selected.
 * Manages separate state for Exec (4 seats) and XL (7 seats) vehicles.
 */
class SilverExecSelectorSection extends Component<PropsFromStore> {
    
    render() {
        // Only show if selected vehicle requires the Silver Exec selector
        if (!this.props.SelectedCondition.Service.requiresSilverExecSelector) {
            return null;
        }

        // Determine if this is Exec (4 seats) or XL (7 seats)
        const maxSeat = this.props.SelectedCondition.ApiVehicle?.MaxSeat || 4;
        const isXL = maxSeat === 7;

        // Get the appropriate Redux state
        const reduxData = isXL ? this.props.SilverExecXl : this.props.SilverExec;

        // Use Redux values or defaults
        const passengers = reduxData?.Passengers ?? (isXL ? 5 : 1);
        const luggage = reduxData?.Luggage ?? 0;
        const flightNumber = reduxData?.FlightNumber ?? "";

        return (
            <Box 
                sx={{ 
                    paddingTop: 2, 
                    paddingBottom: 2, 
                    paddingLeft: "24px", 
                    paddingRight: "24px", 
                    backgroundColor: "#f9f9f9", 
                    borderBottom: "1px solid #ddd",
                    marginBottom: 2
                }}
            >
                <SilverExecSelector
                    MaxCapacity={maxSeat}
                    PassengerCount={passengers}
                    LuggageCount={luggage}
                    FlightNumber={flightNumber}
                    IsPriceGuaranteeSelected={this.props.IsPriceGuaranteeSelected}
                    BookingTime={this.props.BookingTime}
                    PickupTimeZoneId={this.props.PickupTimeZoneId}
                    PickupZoneId={this.props.PickupZoneId}
                    OnPassengerChange={(count: number) => {
                        if (isXL) {
                            Dispatch.Booking.UpdateSilverExecXlPassengers(count);
                        } else {
                            Dispatch.Booking.UpdateSilverExecPassengers(count);
                        }
                    }}
                    OnLuggageChange={(count: number) => {
                        if (isXL) {
                            Dispatch.Booking.UpdateSilverExecXlLuggage(count);
                        } else {
                            Dispatch.Booking.UpdateSilverExecLuggage(count);
                        }
                    }}
                    OnFlightNumberChange={(flight: string) => {
                        if (isXL) {
                            Dispatch.Booking.UpdateSilverExecXlFlightNumber(flight);
                        } else {
                            Dispatch.Booking.UpdateSilverExecFlightNumber(flight);
                        }
                    }}
                />
            </Box>
        );
    }
}

function mapStateToProps(state: ApplicationState): PropsFromStore {
    const pickupServiceCheck = state.booking.PickupServiceCheck;
    const pickupTimeZoneId = (pickupServiceCheck.status === ServiceCheckStatus.KnownGood) 
        ? pickupServiceCheck.TimeZoneId 
        : null;
    
    const pickupZoneId = state.booking.Locations[0]?.CuratedLocationZoneId ?? null;

    return {
        SelectedCondition: state.condition.SelectedCondition,
        IsPriceGuaranteeSelected: state.condition.IsPriceGuaranteeSelected,
        SilverExec: state.booking.SilverExec,
        SilverExecXl: state.booking.SilverExecXl,
        BookingTime: state.booking.BookingTimeV2,
        PickupTimeZoneId: pickupTimeZoneId,
        PickupZoneId: pickupZoneId,
    };
}

export default connect(mapStateToProps)(SilverExecSelectorSection);
